import { OVERLAY_MOTION } from './motion/overlayMotionTokens';

export { OVERLAY_MOTION };

/** Shared enter/exit motion for centered modals (transfer, confirm, etc.). */

/**
 * Unified bottom-sheet slide motion. Shared by BottomSheet (A),
 * CustomDateCalendarOverlay (B), and LineItemPicker (C) so every mobile sheet
 * enters/exits with the same speed + curve. Exit is the same duration (the
 * `useOverlayTransition` unmount delay reuses it). Confident ease-out-expo.
 */
export const SHEET_MOTION = {
  durationMs: OVERLAY_MOTION.slideEnterMs,
  exitMs: OVERLAY_MOTION.slideExitMs,
  easing: OVERLAY_MOTION.easing,
};

/**
 * One timing for every tween inside LineItemPicker's search-filter morph
 * (row collapse, section-header fold, card-corner join, AutoHeight resize).
 * Shares SHEET_MOTION's easing so the morph reads as the same "confident
 * ease-out-expo" family as the sheet's own enter/exit.
 */
export const PICKER_MORPH = {
  durationMs: 240,
  easing: SHEET_MOTION.easing,
};

export const MODAL_MOTION = {
  durationMs: { mobile: 280, desktop: 220, fade: 220, pop: 220 },
  easing: OVERLAY_MOTION.easing,
  /** Snappier curve on touch — matches Collapsible / AutoHeight mobile. */
  easingMobile: 'cubic-bezier(0.22, 1, 0.36, 1)',
  /** Calm, no-overshoot curve for the plain opacity fade (close-session confirm on mobile). */
  easingFade: 'cubic-bezier(0.4, 0, 0.2, 1)',
};

export function prefersCoarsePointer() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
}

export function modalMotionDuration(preferMobile = prefersCoarsePointer(), scaleMotion = false, fadeMotion = false, popMotion = false) {
  if (popMotion) return MODAL_MOTION.durationMs.pop;
  if (fadeMotion) return MODAL_MOTION.durationMs.fade;
  if (scaleMotion || !preferMobile) return MODAL_MOTION.durationMs.desktop;
  return MODAL_MOTION.durationMs.mobile;
}

export function modalScrimMotionStyle(visible, durationMs) {
  const ms = durationMs;
  return {
    opacity: visible ? 1 : 0,
    pointerEvents: visible ? 'auto' : 'none',
    transition: `opacity ${ms}ms ${OVERLAY_MOTION.easing}`,
  };
}

/** Run `callback` after the standard modal exit animation completes. */
export function deferAfterModalExit(callback) {
  const ms = modalMotionDuration();
  if (typeof window === 'undefined') {
    callback();
    return undefined;
  }
  return window.setTimeout(callback, ms);
}

/**
 * Centred pop-up panel: fade + scale .98 -> 1 on the one overlay curve, exit a
 * little quicker. `fadeMotion` is the plain opacity variant (no scale). Phone,
 * tablet and desktop all use it — pop-ups are centred everywhere.
 */
export function modalPanelMotionStyle(visible, durationMs, preferMobile = prefersCoarsePointer(), scaleMotion = false, fadeMotion = false) {
  const { easing, popExitMs, popFromScale } = OVERLAY_MOTION;
  const ms = visible ? durationMs : Math.min(durationMs, popExitMs);
  const hidden = fadeMotion ? 'translate3d(0, 0, 0)' : `translate3d(0, 0, 0) scale(${popFromScale})`;
  const shown = fadeMotion ? 'translate3d(0, 0, 0)' : 'translate3d(0, 0, 0) scale(1)';
  return {
    opacity: visible ? 1 : 0,
    transform: visible ? shown : hidden,
    transition: fadeMotion
      ? `opacity ${ms}ms ${easing}`
      : `opacity ${ms}ms ${easing}, transform ${ms}ms ${easing}`,
    willChange: visible ? (fadeMotion ? 'opacity' : 'transform, opacity') : 'auto',
    backfaceVisibility: 'hidden',
  };
}
